import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRepackings } from '@hooks/useRepacking'

const statusColors: Record<string, string> = {
  IN_PROGRESS: 'badge-warning',
  COMPLETED: 'badge-success',
  ON_HOLD: 'badge-danger',
}

const statusLabels: Record<string, string> = {
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  ON_HOLD: 'On Hold',
}

export function RepackingListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useRepackings(search, status)

  const repackings = data?.data?.items || []
  const total = data?.data?.total || 0
  const pageSize = data?.data?.pageSize || 20
  const totalPages = data?.data?.totalPages || 0

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Repacking Jobs</h1>
          <p className="text-gray-600 text-sm mt-1">
            Convert bulk batches into pack sizes
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/repacking/create')}
          className="btn-primary"
        >
          + Start Repacking Job
        </button>
      </div>

      <div className="card">
        <div className="space-y-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Search by batch or product..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full"
            />

            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setPage(1)
              }}
              className="w-full"
            >
              <option value="">All Statuses</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="ON_HOLD">On Hold</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            Failed to load repacking jobs. Please try again.
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : repackings.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No repacking jobs found.' : 'No repacking jobs yet.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 font-semibold">Batch No.</th>
                    <th className="text-left px-4 py-2 font-semibold">Product</th>
                    <th className="text-right px-4 py-2 font-semibold">Input Qty</th>
                    <th className="text-right px-4 py-2 font-semibold">Expected Yield</th>
                    <th className="text-right px-4 py-2 font-semibold">Actual Output</th>
                    <th className="text-right px-4 py-2 font-semibold">Loss %</th>
                    <th className="text-left px-4 py-2 font-semibold">Repacker</th>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-left px-4 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {repackings.map((repacking) => (
                    <tr
                      key={repacking.id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-3 font-mono font-medium">
                        {repacking.bulkBatch.batchNo}
                      </td>
                      <td className="px-4 py-3">{repacking.bulkBatch.product.name}</td>
                      <td className="px-4 py-3 text-right">
                        {repacking.inputQty.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {repacking.expectedYield.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {repacking.actualOutput.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`font-medium ${
                            repacking.lossPercentage &&
                            repacking.lossPercentage >
                              repacking.bulkBatch.product.expectedLossPct
                              ? 'text-red-600'
                              : 'text-green-600'
                          }`}
                        >
                          {repacking.lossPercentage?.toFixed(1) || '-'}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {repacking.repackerUser.fullName}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${statusColors[repacking.status]}`}>
                          {statusLabels[repacking.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/repacking/${repacking.id}`
                            )
                          }
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing {(page - 1) * pageSize + 1} to{' '}
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
